import ScrollModal from "@/components/common/scroll-modal";
import {
  filterTypesEnum,
  searchTypesEnum,
  type FilterDateType,
  type FilterOption,
  type FilterSearchType,
  type FilterStatusType,
  type CatalogSearchItem,
  type FilterHierarchyStatusType,
} from "@/types/common/filters";
import type { UserType } from "@/types/entities/users";
import { formatToYMD, parseLocalDateFromString } from "@/utils/dates";
import { deepEqual } from "@/utils/filters";
import { Button, Heading, Icon } from "@elaniintc/goes-design-system";
import { PageTabs } from "@/components/ui/page-tabs";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import FilterDateRange from "./FilterDateRange";
import FilterSearch from "./FilterSearch";
import FiltersSidebar from "./FiltersSidebar";
import FilterState from "./FilterStatus";
import type { RoleType } from "@/types/entities/roles";
import FilterBasicSearch from "./FilterBasicSearch";
import type { TagType } from "@/types/entities/tags";
import { cn } from "@/utils/theme";
import FilterHierarchyStatus from "./FilterHierarchyStatus";

interface FiltersProps {
  isOpen: boolean;
  onToggle: (type: string) => void;
  items: FilterOption[];
  onApply: (newOptions: FilterOption[]) => void;
  hasCurrentFilters: boolean;
}

export const FiltersModal = ({
  isOpen,
  onToggle,
  items,
  onApply,
  hasCurrentFilters,
}: FiltersProps) => {
  const [filterItems, setFilterItems] = useState<FilterOption[]>(items);

  const [selectedFilter, setSelectedFilter] = useState<FilterOption>(
    filterItems[0],
  );
  const [isDifferent, setIsDifferent] = useState(false);

  useEffect(() => {
    if (filterItems.some((filter) => filter.isUpdated === true)) {
      if (!isDifferent) setIsDifferent(true);
    } else if (isDifferent) setIsDifferent(false);
  }, [filterItems, isDifferent]);

  const handleUpdateFilters = (newFilter: FilterOption) => {
    const updatedFilters = filterItems.filter(
      (item) => item.id !== newFilter.id,
    );
    const filterToUpdate = items.find((item) => item.id === newFilter.id);
    if (!filterToUpdate) return;

    const areEqual = deepEqual<FilterOption>(
      { ...filterToUpdate, isUpdated: false },
      { ...newFilter, isUpdated: false },
    );
    newFilter = { ...newFilter, isUpdated: !areEqual };
    const sortFilters = [...updatedFilters, newFilter].sort(
      (a, b) => a.id - b.id,
    );

    setFilterItems(sortFilters);
    setSelectedFilter(newFilter);
  };

  return (
    <>
      <ScrollModal
        isOpen={isOpen}
        onClose={() => onToggle("close")}
        modalContentClassName="max-w-[340px] tablet:max-w-[640px] desktop:max-w-[764px] h-[616px] w-full p-0 gap-0"
        modalHeaderClassName="bg-neutral-foreground"
        modalFooterClassName="bg-neutral-foreground"
        header={
          <div className="flex items-center justify-between p-4">
            <Heading variant="h5" className="mb-0!">
              Filtros
            </Heading>
            <Button
              type="button"
              onClick={() => onToggle("close")}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" className="text-sm text-gray-500" />
            </Button>
          </div>
        }
        footer={
          <div className="flex w-full items-center justify-between p-4">
            <Button
              type="button"
              onClick={() => onToggle("clean")}
              variant="quiet"
              aria-label="Limpiar"
              disabled={!hasCurrentFilters}
              className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
            >
              Limpiar filtros
            </Button>
            <div className="flex space-x-3">
              <Button
                type="button"
                onClick={() => onToggle("close")}
                variant="quiet"
                aria-label="Cancelar"
                className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={() => onApply(filterItems)}
                variant="default"
                aria-label="Applicar"
                className="cursor-pointer min-w-10 text-base font-semibold leading-3xl font-geist"
                disabled={!isDifferent}
              >
                Aplicar
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex tablet:justify-between flex-col tablet:flex-row overflow-hidden flex-1 justify-start">
          <div
            className={cn(
              "hidden tablet:flex tablet:flex-col tablet:w-60 laptop:w-90 laptop:mi n-h-112 border-r-[0.5px] border-neutral-silent",
            )}
          >
            {filterItems.map((filter) => {
              return (
                <FiltersSidebar
                  label={filter.label}
                  active={filter.id === selectedFilter.id}
                  key={filter.id}
                  isUpdated={filter.isUpdated || filter.result !== ""}
                  onSelected={() => setSelectedFilter(filter)}
                />
              );
            })}
          </div>
          <div className="tablet:hidden flex overflow-x-auto">
            <PageTabs
              activeKey={selectedFilter.id.toString()}
              onTabChange={(key) => {
                const filter = filterItems.find(
                  (item) => item.id.toString() === key,
                );
                if (filter) setSelectedFilter(filter);
              }}
              tabs={filterItems.map(({ label, id }) => {
                return { label, key: id.toString() };
              })}
              className="w-full min-w-max"
            />
          </div>
          <div className="w-full tablet:w-101">
            <FiltersDisplay
              item={selectedFilter}
              onUpdateFilters={handleUpdateFilters}
            />
          </div>
        </div>
      </ScrollModal>
    </>
  );
};

const FiltersDisplay = ({
  item,
  onUpdateFilters,
}: {
  item: FilterOption;
  onUpdateFilters: (value: FilterOption) => void;
}) => {
  const handleUpdateState = (value: string) => {
    let newFilters = item as FilterStatusType;
    const updatedStatus = newFilters.status.map((state) => {
      if (state.label === value) {
        return { ...state, value: !state.value };
      }
      return state;
    });

    const selectedFields = updatedStatus
      .filter((state) => state.value)
      .map((state) => state.field);

    let newResult = "";
    switch (value) {
      case "all":
        newFilters = {
          ...newFilters,
          result: newFilters.status.map((status) => status.field).join(","),
          status: newFilters.status.map((state) => ({
            ...state,
            value: true,
          })),
        };
        break;
      case "none":
        newFilters = {
          ...newFilters,
          result: "",
          status: newFilters.status.map((state) => ({
            ...state,
            value: false,
          })),
        };
        break;
      default:
        newResult = selectedFields.join(",");
        newFilters = {
          ...newFilters,
          status: updatedStatus,
          result: newResult,
        };
        break;
    }
    onUpdateFilters({ ...item, ...newFilters });
  };

  const handleUpdateDate = (
    type: string,
    newDate: Date | undefined,
    toDate?: Date | undefined,
  ) => {
    let newFilters = item as FilterDateType;
    const allDates = newFilters.result.split(",");
    switch (type) {
      case "desde":
        if (!newDate) {
          newFilters = { ...newFilters, result: "" };
        } else {
          const formatDate = formatToYMD(newDate);
          if (newFilters.result) {
            if (allDates.length > 1) {
              newFilters = {
                ...newFilters,
                result: formatDate + "," + allDates[1],
              };
            } else newFilters = { ...newFilters, result: formatDate };
          } else newFilters = { ...newFilters, result: formatDate };
        }
        break;
      case "hasta":
        if (!newDate && allDates.length > 1) {
          newFilters = { ...newFilters, result: allDates[0] };
        } else {
          if (dayjs(newDate).isBefore(dayjs(allDates[0]))) break;
          const formatDate = formatToYMD(newDate!);
          newFilters = {
            ...newFilters,
            result: allDates[0] + "," + formatDate,
          };
        }
        break;
      case "range": {
        if (newDate) {
          if (toDate) {
            newFilters = {
              ...newFilters,
              result: formatToYMD(newDate) + "," + formatToYMD(toDate),
            };
          } else {
            newFilters = { ...newFilters, result: formatToYMD(newDate) };
          }
        }
        break;
      }
      default:
        break;
    }

    onUpdateFilters({ ...newFilters });
  };

  const handleUpdateSearch = (
    type: "remove" | "add",
    _value: UserType | RoleType | TagType | CatalogSearchItem,
  ) => {
    let newFilter = item as FilterSearchType;
    if (!_value) return;

    if (newFilter.searchType === searchTypesEnum.user) {
      if (type === "add") {
        const value = _value as UserType;
        const selectIds = newFilter.result
          ? newFilter.result + "," + value.id
          : value.id;
        let newSelected: UserType[] = [];
        if (newFilter.result) {
          newSelected = [...(newFilter.selected as UserType[]), value];
        } else newSelected = [value];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: newSelected,
        };
      } else {
        const value = _value as UserType;
        const selectIds = newFilter.result
          .split(",")
          .filter((id) => id !== value.id)
          .join(",");
        const selectedUsers = [
          ...(newFilter.selected as UserType[]).filter(
            (user) => user.id !== value.id,
          ),
        ];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: selectedUsers,
        };
      }
    }

    if (newFilter.searchType === searchTypesEnum.roles) {
      if (type === "add") {
        const value = _value as RoleType;
        const selectIds = newFilter.result
          ? newFilter.result + "," + value.id
          : value.id;
        let newSelected: RoleType[] = [];
        if (newFilter.result) {
          newSelected = [...(newFilter.selected as RoleType[]), value];
        } else newSelected = [value];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: newSelected,
        };
      } else {
        const value = _value as RoleType;
        const selectIds = newFilter.result
          .split(",")
          .filter((id) => id !== value.id)
          .join(",");
        const selectedRoles = [
          ...(newFilter.selected as RoleType[]).filter(
            (role) => role.id !== value.id,
          ),
        ];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: selectedRoles,
        };
      }
    }

    if (newFilter.searchType === searchTypesEnum.tags) {
      if (type === "add") {
        const value = _value as TagType;
        const selectIds = newFilter.result
          ? newFilter.result + "," + value.id
          : value.id;
        let newSelected: TagType[] = [];
        if (newFilter.result) {
          newSelected = [...(newFilter.selected as TagType[]), value];
        } else newSelected = [value];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: newSelected,
        };
      } else {
        const value = _value as TagType;
        const selectIds = newFilter.result
          .split(",")
          .filter((id) => id !== value.id)
          .join(",");
        const selectedTags = [
          ...(newFilter.selected as TagType[]).filter(
            (tag) => tag.id !== value.id,
          ),
        ];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: selectedTags,
        };
      }
    }

    if (
      newFilter.searchType === searchTypesEnum.positions ||
      newFilter.searchType === searchTypesEnum.institutions
    ) {
      if (type === "add") {
        const value = _value as CatalogSearchItem;
        const selectIds = newFilter.result
          ? newFilter.result + "," + value.id
          : value.id;
        const newSelected = newFilter.result
          ? [...(newFilter.selected as CatalogSearchItem[]), value]
          : [value];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: newSelected,
        };
      } else {
        const value = _value as CatalogSearchItem;
        const selectIds = newFilter.result
          .split(",")
          .filter((id) => id !== value.id)
          .join(",");
        const selectedCatalogs = [
          ...(newFilter.selected as CatalogSearchItem[]).filter(
            (catalog) => catalog.id !== value.id,
          ),
        ];
        newFilter = {
          ...newFilter,
          result: selectIds,
          selected: selectedCatalogs,
        };
      }
    }

    onUpdateFilters({ ...newFilter });
  };

  const handleUpdateSearchFromResult = (
    newItems: UserType[] | RoleType[] | CatalogSearchItem[],
  ) => {
    let newFilter = item as FilterSearchType;
    if (newFilter.searchType === searchTypesEnum.user) {
      const selectIds = newItems.map((item) => item.id).join(",");
      newFilter = {
        ...newFilter,
        result: selectIds,
        selected: newItems as UserType[],
      };
    }

    if (newFilter.searchType === searchTypesEnum.roles) {
      const selectIds = newItems.map((item) => item.id).join(",");
      newFilter = {
        ...newFilter,
        result: selectIds,
        selected: newItems as RoleType[],
      };
    }

    if (
      newFilter.searchType === searchTypesEnum.positions ||
      newFilter.searchType === searchTypesEnum.institutions
    ) {
      const selectIds = newItems.map((item) => item.id).join(",");
      newFilter = {
        ...newFilter,
        result: selectIds,
        selected: newItems as CatalogSearchItem[],
      };
    }

    onUpdateFilters({ ...newFilter });
  };

  const handleBasicSearch = (value: string) => {
    let newFilter = item as FilterOption;
    newFilter = {
      ...newFilter,
      result: value,
    };
    onUpdateFilters({ ...newFilter });
  };

  const handleHierarchyStatus = (value: string, parentLabel?: string) => {
    let newFilter = item as FilterHierarchyStatusType;

    newFilter = {
      ...newFilter,
      isUpdated: true,
      groups: newFilter.groups.map((group) => ({
        ...group,
        status: group.status.map((parent) => {
          if (parent.label === value && !parentLabel) {
            const nextValue = !parent.value;

            return {
              ...parent,
              value: nextValue,
              children: parent.children?.map((c) => ({
                ...c,
                value: nextValue,
              })),
            };
          }

          const shouldUpdateThisParent = parentLabel
            ? parent.label === parentLabel
            : true;

          if (!shouldUpdateThisParent) return parent;

          const updatedChildren = parent.children?.map((child) => {
            if (child.label === value) {
              return { ...child, value: !child.value };
            }
            return child;
          });

          const parentValue = updatedChildren
            ? updatedChildren.every((c) => c.value)
            : parent.value;

          return {
            ...parent,
            value: parentValue,
            children: updatedChildren,
          };
        }),
      })),
    };

    onUpdateFilters({ ...newFilter });
  };

  if (item.type === filterTypesEnum.status) {
    return (
      <FilterState states={item.status} onStatesChange={handleUpdateState} />
    );
  }

  if (item.type === filterTypesEnum.dates) {
    const dates = item.result ? item.result.split(",") : [undefined, undefined];
    return (
      <FilterDateRange
        initialDate={dates[0] ? parseLocalDateFromString(dates[0]) : undefined}
        endDate={dates[1] ? parseLocalDateFromString(dates[1]) : undefined}
        title={item.label}
        shouldDisabledFutureDates={item.shouldDisabledFutureDates}
        onDatesChange={handleUpdateDate}
      />
    );
  }

  if (item.type === filterTypesEnum.textsearch && item.query) {
    return (
      <FilterSearch
        initialSearchText=""
        searchType={item.searchType}
        searchField={item.searchField}
        title={item.label}
        result={item.result}
        useQueryHook={item.query!}
        onUpdateSearch={handleUpdateSearch}
        selected={item.selected || []}
        onUpdateSearchFromResult={handleUpdateSearchFromResult}
      />
    );
  }

  if (item.type === "basicsearch") {
    return <FilterBasicSearch onChange={handleBasicSearch} />;
  }

  if (item.type === filterTypesEnum.hierarchyStatus) {
    return (
      <FilterHierarchyStatus
        groups={item.groups}
        filterLabel={item.label}
        onStatesChange={handleHierarchyStatus}
      />
    );
  }
};
